/**
 * Shared client-side password policy, mirroring the backend {@code PasswordPolicy}:
 * at least {@link MIN_LENGTH} characters including an uppercase letter, a lowercase
 * letter, a digit and a special character.
 */
export const MIN_LENGTH = 8;

const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const LOWER = 'abcdefghijkmnpqrstuvwxyz';
const DIGIT = '23456789';
const SPECIAL = '!@#$%&*?-_';

/** True when the password satisfies the composition policy. */
export function isPasswordValid(password: string): boolean {
  return (
    password.length >= MIN_LENGTH &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[^A-Za-z0-9]/.test(password)
  );
}

function pick(pool: string): string {
  const index = Math.floor((crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32) * pool.length);
  return pool.charAt(index);
}

/** Generates a policy-compliant password that is easy to copy and share. */
export function generatePassword(length = 14): string {
  const size = Math.max(length, MIN_LENGTH);
  const all = UPPER + LOWER + DIGIT + SPECIAL;
  const chars = [pick(UPPER), pick(LOWER), pick(DIGIT), pick(SPECIAL)];
  while (chars.length < size) {
    chars.push(pick(all));
  }
  // Shuffle so the guaranteed categories are not always at the front.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor((crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32) * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}
